import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Form,
  Button,
  Container,
  Row,
  Col,
  Card,
  InputGroup,
} from "react-bootstrap";
import { FaCalendarAlt, FaClock, FaArrowLeft } from "react-icons/fa";
import Loader from "../../components/Loader";
import { toast } from "react-toastify";
import PropTypes from "prop-types";
import { useCreateEventMutation } from "../../slices/eventApiSlice";
import ErrorBoundary from "../../components/ErrorBoundary";
import moment from "moment-timezone";

// Form Field Component
const FormField = ({
  label,
  type,
  value,
  onChange,
  required,
  error,
  disabled,
}) => (
  <Form.Group className="mb-3">
    <Form.Label>{label}</Form.Label>
    <InputGroup>
      {type.startsWith("date") && (
        <InputGroup.Text>
          <FaCalendarAlt />
        </InputGroup.Text>
      )}
      {type === "time" && (
        <InputGroup.Text>
          <FaClock />
        </InputGroup.Text>
      )}
      <Form.Control
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        disabled={disabled}
        isInvalid={!!error}
      />
      <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>
    </InputGroup>
  </Form.Group>
);

FormField.propTypes = {
  label: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  required: PropTypes.bool,
  error: PropTypes.string,
  disabled: PropTypes.bool,
};

const CLUB_TZ = "America/New_York";

const EventCreateScreen = () => {
  const navigate = useNavigate();
  const [createEvent, { isLoading }] = useCreateEventMutation();

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    date: "",
    startTime: "09:00",
    endTime: "17:00",
    allDay: false,
  });

  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    if (!formData.title.trim()) {
      newErrors.title = "Title is required";
    }
    if (!formData.date) {
      newErrors.date = "Event date is required";
    }

    let startTz, endTz;

    if (!formData.allDay) {
      if (!formData.startTime) {
        newErrors.startTime = "Start time is required";
      }
      if (!formData.endTime) {
        newErrors.endTime = "End time is required";
      }

      if (!newErrors.startTime && !newErrors.endTime && formData.date) {
        startTz = moment.tz(
          `${formData.date} ${formData.startTime}`,
          "YYYY-MM-DD HH:mm",
          CLUB_TZ
        );
        endTz = moment.tz(
          `${formData.date} ${formData.endTime}`,
          "YYYY-MM-DD HH:mm",
          CLUB_TZ
        );

        if (!startTz.isValid()) newErrors.startTime = "Invalid start time";
        if (!endTz.isValid()) newErrors.endTime = "Invalid end time";
        if (startTz.isValid() && endTz.isValid() && !endTz.isAfter(startTz)) {
          newErrors.endTime = "End time must be after start time";
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      const startTime = formData.startTime || "00:00";
      const endTime = formData.endTime || "23:59";
      const startTz = moment.tz(
        `${formData.date} ${startTime}`,
        "YYYY-MM-DD HH:mm",
        CLUB_TZ
      );
      const endTz = moment.tz(
        `${formData.date} ${endTime}`,
        "YYYY-MM-DD HH:mm",
        CLUB_TZ
      );

      const eventData = {
        title: formData.title,
        description: formData.description,
        allDay: formData.allDay,
        start: startTz.utc().toISOString(),
        end: endTz.utc().toISOString(),
      };

      await createEvent(eventData).unwrap();
      toast.success("Event created successfully");
      navigate("/admin/events/list");
    } catch (err) {
      toast.error(err?.data?.message || "An error occurred");
    }
  };

  const handleInputChange = (field) => (e) => {
    const value =
      e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  if (isLoading) return <Loader />;

  return (
    <ErrorBoundary>
      <Container className="py-4">
        <Row className="justify-content-center">
          <Col md={8}>
            <Card className="shadow-sm">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h1 className="mb-0">Create New Event</h1>
                  <Link
                    to="/admin/events/list"
                    className="btn btn-outline-secondary"
                  >
                    <FaArrowLeft className="me-1" /> Back to Events
                  </Link>
                </div>

                <Form onSubmit={handleSubmit}>
                  <FormField
                    label="Event Title"
                    type="text"
                    value={formData.title}
                    onChange={handleInputChange("title")}
                    required
                    error={errors.title}
                  />

                  <Form.Group className="mb-3">
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={formData.description}
                      onChange={handleInputChange("description")}
                    />
                  </Form.Group>

                  <Row className="justify-content-center">
                    <Col md={6}>
                      <FormField
                        label="Event Date"
                        type="date"
                        value={formData.date}
                        onChange={handleInputChange("date")}
                        required
                        error={errors.date}
                      />
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <FormField
                        label="Start Time"
                        type="time"
                        value={formData.startTime}
                        onChange={handleInputChange("startTime")}
                        required={!formData.allDay}
                        disabled={formData.allDay}
                        error={errors.startTime}
                      />
                    </Col>
                    <Col md={6}>
                      <FormField
                        label="End Time"
                        type="time"
                        value={formData.endTime}
                        onChange={handleInputChange("endTime")}
                        required={!formData.allDay}
                        disabled={formData.allDay}
                        error={errors.endTime}
                      />
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Check
                      type="checkbox"
                      label="Do Not Display Time On Event (All Day Event)"
                      checked={formData.allDay}
                      onChange={handleInputChange("allDay")}
                    />
                  </Form.Group>

                  <div className="d-grid">
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={isLoading}
                    >
                      {isLoading ? <Loader size="sm" /> : "Create Event"}
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </ErrorBoundary>
  );
};

export default EventCreateScreen;
